import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {MODEL_RUN_SOURCE} from 'constants/AnalyticsConstants';
import {getScientificAIDisabledMessage} from 'model-hub/permissions/ModelHubPermissions';
import {BoltzGenFormContent} from 'model-hub/scientific-models/input-forms/boltzgen/BoltzGenInputForm';
import * as logger from 'util/Logging';
import {assertNever} from 'util/Types';

import GenerativeModelRunDetails from './GenerativeModelRunDetails';
import {BoltzGenTargetType} from './GenerativeModelsConstants';
import {useGetGenerativeModelRuns, GenerativeModelsRunsList} from './GenerativeModelsRunsList';
import {type GenerativeModelRun} from './GenerativeModelsRunsList';

enum GenerativeModelsViewType {
  FORM = 'FORM',
  RUNS_LIST = 'RUNS_LIST',
  RUN_DETAILS = 'RUN_DETAILS',
}

interface GenerativeModelsViewProps {
  currentEntityType: BoltzGenTargetType;
  targetId: string;
}

export default function GenerativeModelsView({
  currentEntityType,
  targetId,
}: GenerativeModelsViewProps): JSX.Element | null {
  const [currentView, setCurrentView] = useState<GenerativeModelsViewType>(
    GenerativeModelsViewType.RUNS_LIST
  );
  const [selectedRun, setSelectedRun] = useState<GenerativeModelRun | null>(null);

  const {pendingRuns, completedRuns, loading, error, refetch} = useGetGenerativeModelRuns({
    currentEntityType,
    targetId,
  });

  if (!BenchlingConfig.MODEL_HUB_ENABLE_GENERATIVE_MODELS_TAB) {
    return <div>Generative models are not enabled</div>;
  }

  function renderListView(): JSX.Element {
    if (error) {
      return <div>Failed to load runs</div>;
    }
    const disabledMessage = getScientificAIDisabledMessage() ?? undefined;
    return (
      <GenerativeModelsRunsList
        loading={loading}
        pendingRuns={pendingRuns}
        onRunSelected={(run: GenerativeModelRun) => {
          setSelectedRun(run);
          setCurrentView(GenerativeModelsViewType.RUN_DETAILS);
        }}
        completedRuns={completedRuns}
        onCreateNewRun={() => {
          setCurrentView(GenerativeModelsViewType.FORM);
        }}
        disabledMessage={disabledMessage}
      />
    );
  }

  switch (currentView) {
    case GenerativeModelsViewType.FORM:
      return (
        <BoltzGenFormContent
          targetEntity={{entityType: currentEntityType, entityId: targetId}}
          navigateToList={() => {
            setCurrentView(GenerativeModelsViewType.RUNS_LIST);
          }}
          onSubmit={() => {
            refetch();
            setCurrentView(GenerativeModelsViewType.RUNS_LIST);
          }}
          source={MODEL_RUN_SOURCE.STRUCTURE_TAB}
        />
      );
    case GenerativeModelsViewType.RUNS_LIST:
      return renderListView();
    case GenerativeModelsViewType.RUN_DETAILS:
      if (!selectedRun) {
        logger.warn('No selected run when switching to run details view');
        return renderListView();
      }
      return (
        <GenerativeModelRunDetails
          run={selectedRun}
          navigateToList={() => {
            setCurrentView(GenerativeModelsViewType.RUNS_LIST);
          }}
        />
      );
    default:
      assertNever(currentView);
      return null;
  }
}
